import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { DesiredDates } from 'ha/models/BookingDates/types';
import { DisplayedPriceInfo } from 'ha/models/Listing/types';
import { getUtilityBillsFromCosts } from 'ha/models/ListingUtilityBills/getUtilityBillsFromCosts';
import { PricingStructure } from 'ha/modules/Pricing';
import {
  ListingPricingType,
  PricingMonthlyKeys,
} from '@ha/api/v2/types/Listing';

import {
  getPrice,
  getCurrency,
  getCosts,
  getRentalConditions,
  getDesiredDates,
  getPricingValues,
  getPricingType,
  getListingIsCharitable,
  getListingIsFree,
  getIsBelongsToPartner,
} from '../selectors';
import { useLocalPricing } from './useLocalPricing';
import { useDesiredDates } from './useDesiredDates';

const getAllMonthsBetweenTwoDates = (dates: DesiredDates) => {
  let startDate = dayjs(dates.startDate ?? undefined).set('date', 1);
  const endDate = dayjs(dates.endDate ?? undefined).set('date', 2);

  const result = [];
  while (startDate.isBefore(endDate)) {
    result.push(startDate.format('MM'));
    startDate = startDate.add(1, 'month');
  }
  return result;
};

export function useDisplayedPrice(): DisplayedPriceInfo {
  const price = useSelector(getPrice);
  const { costs } = useSelector(getCosts);
  const currency = useSelector(getCurrency);
  const rentalConditions = useSelector(getRentalConditions);
  const desiredDates = useSelector(getDesiredDates);
  const pricingValues = useSelector(getPricingValues);
  const pricingType = useSelector(getPricingType);
  const isCharitable = useSelector(getListingIsCharitable);
  const isFreeListing = useSelector(getListingIsFree);
  const isBelongsToPartner = useSelector(getIsBelongsToPartner);
  const bills = useMemo(() => getUtilityBillsFromCosts(costs), [costs]);
  const { pricingStructure } = useLocalPricing();

  const { hasDesiredDates } = useDesiredDates();

  if (isCharitable && isFreeListing) {
    return { type: 'free' };
  }

  const showFromPrice =
    pricingStructure === PricingStructure.instalment &&
    !hasDesiredDates &&
    !isBelongsToPartner &&
    pricingType === ListingPricingType.MONTHLY;

  if (showFromPrice) {
    const allPrices = Object.values(pricingValues.monthly ?? {});
    const minPrice = Math.min(...allPrices);

    return { type: 'fromPrice', value: minPrice, bills, currency };
  }

  const showPriceRange =
    pricingStructure === PricingStructure.instalment &&
    hasDesiredDates &&
    pricingType === ListingPricingType.MONTHLY;

  if (showPriceRange) {
    const selectedMonths = getAllMonthsBetweenTwoDates(desiredDates);
    const selectedMonthPrices = selectedMonths.map(
      (month: string) =>
        pricingValues.monthly?.[`m-${month}` as PricingMonthlyKeys] ?? 0,
    );

    const minPrice = Math.min(...selectedMonthPrices);
    const maxPrice = Math.max(...selectedMonthPrices);

    if (minPrice === maxPrice) {
      return { type: 'flatPrice', value: minPrice, bills, currency };
    }

    return {
      type: 'priceRange',
      valueFrom: minPrice,
      valueTo: maxPrice,
      bills,
      currency,
    };
  }

  const firstInstalment = rentalConditions?.firstInstalment;

  return {
    type: 'flatPrice',
    value:
      pricingStructure === PricingStructure.instalment && firstInstalment
        ? firstInstalment
        : price,
    bills,
    currency,
  };
}
